/**
 * AB 实验表格：与多维「表格-条件格式-条形图」一致的占比条配置。
 * 单元格宽度由 BarPercentCell 根据 bar_percent 或 value 解析，此处不修改表格数据。
 */
import type { ITableColumnInfo } from '@/pages/datalab/components/CustomComponents/AliTableAnalytics/types';

/** 允许展示占比条的指标列表 */
const ALLOW_METRIC_VALUE_LIST = ['用户占比', '留存率'];

/** 与多维 ConditionalBarChart 新建项默认主题一致 */
const DEFAULT_BAR_COLOR_THEME = {
  type: 'solidColor' as const,
  color: [{ hexa: '#E0EAFD', alpha: '100' }],
};

const DAY_COL_RE = /^day\d+$/i;

type TBarPercentItem = {
  id: string;
  table_code: string;
  metric: string;
  colorTheme: typeof DEFAULT_BAR_COLOR_THEME;
  min: null;
  max: null;
  allowList?: string[];
};

/**
 * 构建与 setColumnConditionalFormatting 匹配的 conditionalFormat。
 * 白名单与「接口返回的 metric_name」一致，仅对这些指标行/列启用占比条。
 */
export function buildAbExperimentConditionalFormat(
  columnName: ITableColumnInfo[],
  isTranspose: boolean,
): { table_bar_percentage: TBarPercentItem[]; } | undefined {
  if (!columnName.length) {
    return undefined;
  }
  const items: TBarPercentItem[] = [];
  if (!isTranspose) {
    columnName.forEach((col, index) => {
      if (!DAY_COL_RE.test(String(col.origin_name))) {
        return;
      }
      items.push({
        id: `ab-day-${col.origin_name}-${index}`,
        table_code: col.table_code || 't0',
        metric: col.origin_name,
        colorTheme: DEFAULT_BAR_COLOR_THEME,
        min: null,
        max: null,
        allowList: ALLOW_METRIC_VALUE_LIST,
      });
    });
  } else {
    columnName.forEach((col, index) => {
      if (col.type !== 0 || !ALLOW_METRIC_VALUE_LIST.includes(col?.origin_name)) {
        return;
      }
      items.push({
        id: `ab-metric-${col.origin_name}-${index}`,
        table_code: col.table_code || 't0',
        metric: col.origin_name,
        colorTheme: DEFAULT_BAR_COLOR_THEME,
        min: null,
        max: null,
        // 与非转置一致：用 cell.value 作为占比；见 BarPercentCell
        allowList: ALLOW_METRIC_VALUE_LIST,
      });
    });
  }

  if (!items.length) {
    return undefined;
  }
  return { table_bar_percentage: items };
}